import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { playTTS } from "@/lib/tts";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { X, Volume2, Plus, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Module-level cache so re-opening the same word/sentence during a session
// (e.g. re-clicking a word you just looked at) renders instantly with no network wait.
const lookupCache = new Map();

export default function WordCardPanel({ wordClick, video, onClose, onAdded }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const { user } = useAuth();
  const abortRef = useRef(null);

  useEffect(() => {
    if (!wordClick) return;
    abortRef.current?.abort();
    const cacheKey = `${wordClick.word}|${wordClick.sentence}`;
    const cached = lookupCache.get(cacheKey);
    if (cached) {
      setData(cached);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setData(null);
    api
      .post("/words/lookup", { word: wordClick.word, sentence: wordClick.sentence }, { signal: controller.signal })
      .then(({ data }) => {
        lookupCache.set(cacheKey, data);
        setData(data);
      })
      .catch((e) => {
        if (e.code !== "ERR_CANCELED" && e.response?.status !== 401) toast.error("Не удалось получить перевод слова");
      })
      .finally(() => {
        setLoading(false);
      });
    return () => controller.abort();
  }, [wordClick]);

  const speak = () => {
    if (!data) return;
    playTTS(data.word, user?.settings);
  };

  const handleAdd = async () => {
    setAdding(true);
    try {
      await api.post("/words", {
        word: data.word,
        translation: data.translation,
        transcription: data.transcription,
        part_of_speech: data.part_of_speech,
        definition: data.definition,
        sentence: wordClick.sentence,
        video_id: video.id,
        video_title: video.title,
        timestamp: wordClick.timestamp,
        youtube_id: video.youtube_id,
      });
      setData((d) => {
        const updated = { ...d, already_added: true };
        lookupCache.set(`${wordClick.word}|${wordClick.sentence}`, updated);
        return updated;
      });
      toast.success(`Слово "${data.word}" добавлено в словарь`);
      onAdded?.();
      setTimeout(() => onClose(), 700);
    } catch (e) {
      toast.error("Не удалось добавить слово");
    } finally {
      setAdding(false);
    }
  };

  return (
    <AnimatePresence>
      {wordClick && (
        <motion.div
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "spring", damping: 28, stiffness: 300 }}
          className="fixed top-0 right-0 h-full w-full sm:w-96 bg-card border-l border-border shadow-2xl z-[10000] flex flex-col"
          data-testid="word-card-panel"
        >
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h3 className="font-heading font-semibold">Карточка слова</h3>
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="word-card-close-button">
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-5">
            {loading ? (
              <div data-testid="word-card-loading" className="space-y-5 animate-pulse">
                <div className="space-y-1">
                  <h2 className="font-heading text-3xl font-extrabold tracking-tight capitalize">
                    {wordClick.word}
                  </h2>
                  <div className="h-3 w-24 bg-muted rounded" />
                </div>
                <div className="h-4 w-16 bg-muted rounded" />
                <div className="space-y-2">
                  <div className="h-3 w-20 bg-muted rounded" />
                  <div className="h-5 w-32 bg-muted rounded" />
                </div>
                <div className="space-y-2">
                  <div className="h-3 w-24 bg-muted rounded" />
                  <div className="h-4 w-full bg-muted rounded" />
                  <div className="h-4 w-3/4 bg-muted rounded" />
                </div>
              </div>
            ) : data ? (
              <>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <h2 className="font-heading text-3xl font-extrabold tracking-tight" data-testid="word-card-word">
                      {data.word}
                    </h2>
                    <button onClick={speak} data-testid="word-card-speak-button" className="text-muted-foreground hover:text-primary transition-colors">
                      <Volume2 className="w-5 h-5" />
                    </button>
                  </div>
                  <p className="text-muted-foreground text-sm" data-testid="word-card-transcription">{data.transcription}</p>
                  <span className="inline-block text-xs uppercase tracking-wide bg-secondary text-secondary-foreground px-2 py-0.5 rounded" data-testid="word-card-pos">
                    {data.part_of_speech}
                  </span>
                </div>

                <div className="space-y-1">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Перевод</p>
                  <p className="text-xl font-semibold" data-testid="word-card-translation">{data.translation}</p>
                </div>

                <div className="space-y-1">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Определение</p>
                  <p className="text-sm leading-relaxed" data-testid="word-card-definition">{data.definition}</p>
                </div>

                <div className="space-y-1 bg-muted/60 rounded-lg p-3">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Контекст</p>
                  <p className="text-sm italic" data-testid="word-card-sentence">"{wordClick.sentence}"</p>
                  <p className="text-xs text-muted-foreground truncate">{video.title}</p>
                  <p className="text-xs text-muted-foreground">{new Date(wordClick.timestamp * 1000).toISOString().substr(14, 5)}</p>
                </div>
              </>
            ) : null}
          </div>

          {data && (
            <div className="p-4 border-t border-border">
              <Button
                className="w-full"
                onClick={handleAdd}
                disabled={adding || data.already_added}
                data-testid="word-card-add-button"
              >
                {adding ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : data.already_added ? (
                  <>
                    <Check className="w-4 h-4 mr-1" /> Уже в словаре
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-1" /> Добавить
                  </>
                )}
              </Button>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
